import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import TitleHeader from '../../components/molecules/TitleHeader/TitleHeader';
import { ASPERATO_STATUSES, PAYMENT_SUB_TYPE } from '../../appConstants';
import { PATHS } from '../../navigationConstants';
import Button from '../../components/atoms/Button/Button';
import Timer from '../../components/atoms/Timer/Timer';
import PaymentSummary from '../../components/atoms/PaymentSummary/PaymentSummary';
import AsperatoIFrame from '../../components/molecules/AsperatoIFrame/AsperatoIFrame';
import Loading from '../../components/atoms/Loading/Loading';
import CheckboxInput from '../../components/atoms/CheckboxInput/CheckboxInput';
import { advancePaymentCopy } from '../../content/main.json';
import {
  getOpportunity,
  getOpportunityPayments,
  getPaymentRecord,
  updateOpportunityExtendedTimeframeAgreed,
} from '../../api/salesforce';
import { extractAsperatoIframeURL } from '../../helpers/utils/generic';
import { UserContext } from '../../providers/UserContext';
import { PaymentContext } from '../../providers/PaymentContext';
import { WalesContext } from '../../providers/WalesContext';
import './AdvancePayment.scss';

const AdvancePayment = ({ history }) => {
  const [opportunityData, setOpportunityData] = useState({});
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState(null);
  const [extendedTimeframeAgreed, setExtendedTimeframeAgreed] = useState(false);
  const [updatingExtendedTimeframe, setUpdatingExtendedTimeframe] = useState(false);
  const { opportunityId, isReferencingExempt } = useContext(UserContext);
  const { paymentId } = useContext(PaymentContext);
  const { isWales } = useContext(WalesContext);
  const {
    isAdvancePaymentPaid,
    offerExpiresAt,
  } = opportunityData;
  const advancePaymentTerms = !isWales ? advancePaymentCopy : advancePaymentCopy.wales;
  const {
    title,
    category,
    info,
    iframeMessage,
    timerCompleted,
    paymentDue,
    nextButton,
    extendedTimeframeInfo,
    extendedTimeframeAdditionalInfo,
    extendedTimeframeCheckboxLabel,
  } = advancePaymentTerms;

  const asperatoListener = (e) => {
    if (url && url.includes(e.origin)) {
      switch (e.data) {
        case ASPERATO_STATUSES.SUCCESS:
          history.push(PATHS.ADVANCE_PAYMENT_SUCCESS);
          break;
        case ASPERATO_STATUSES.ERROR:
          history.push(PATHS.ERROR_PAYMENT_FAILED);
          break;
        case ASPERATO_STATUSES.CANCEL:
          history.push(PATHS.ERROR_PAYMENT_FAILED);
          break;
        default:
          if (
            e.data.includes(ASPERATO_STATUSES.VALUE)
            && e.data.toLowerCase().includes('timeout')
          ) {
            history.push(PATHS.ERROR_PAYMENT_TIMEOUT);
          }
      }
    }
  };

  window.addEventListener('message', asperatoListener);

  const handleExtendedTimeframeChange = async () => {
    if (updatingExtendedTimeframe) return;

    const nextValue = !extendedTimeframeAgreed;
    setExtendedTimeframeAgreed(nextValue);
    setUpdatingExtendedTimeframe(true);

    try {
      await updateOpportunityExtendedTimeframeAgreed(opportunityId, nextValue);
    } catch (error) {
      console.error('[AdvancePayment] Unable to update extended timeframe agreed flag.', error);
      setExtendedTimeframeAgreed(!nextValue);
    } finally {
      setUpdatingExtendedTimeframe(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      const data = await getOpportunity(opportunityId);

      // Exempt customers have no holding deposit to pay, so they skip this step entirely.
      if (data.isAdvancePaymentExempt) {
        history.replace(isReferencingExempt ? PATHS.REFERENCING_PROGRESS : PATHS.REFERENCING_INFO);
        return;
      }

      setOpportunityData(data);
      setExtendedTimeframeAgreed(Boolean(data.isExtendedTimeframeAgreed));
      setLoading(false);
    }
    fetchData();
  }, [opportunityId, paymentId]);

  // The backend refuses the holding deposit payment until the extended timeframe has been
  // agreed in Salesforce, so only fetch it once the tick has been saved.
  useEffect(() => {
    if (loading || isAdvancePaymentPaid || !extendedTimeframeAgreed || updatingExtendedTimeframe || url) {
      return;
    }

    async function fetchPaymentUrl() {
      try {
        const { iframeUrl } = paymentId
          ? await getPaymentRecord(paymentId)
          : await getOpportunityPayments(opportunityId, PAYMENT_SUB_TYPE.ADVANCED);
        setUrl(extractAsperatoIframeURL(iframeUrl));
      } catch (error) {
        console.error('[AdvancePayment] Unable to load the holding deposit payment.', error);
      }
    }
    fetchPaymentUrl();
  }, [
    loading,
    isAdvancePaymentPaid,
    extendedTimeframeAgreed,
    updatingExtendedTimeframe,
    url,
    opportunityId,
    paymentId,
  ]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <TitleHeader title={title} category={category} large />
      {/* eslint-disable-next-line react/no-danger */}
      <p dangerouslySetInnerHTML={{ __html: info }} />
      <p>{extendedTimeframeInfo}</p>
      <CheckboxInput
        id="Extended_Timeframe_Agreed__c"
        name="Extended_Timeframe_Agreed__c"
        value="Extended_Timeframe_Agreed__c"
        className="advancePayment__extendedTimeframeCheckbox"
        label={extendedTimeframeCheckboxLabel}
        checked={extendedTimeframeAgreed}
        onChange={handleExtendedTimeframeChange}
        testId="extended-timeframe-agreed-checkbox"
      />
      <p>{extendedTimeframeAdditionalInfo}</p>

      {extendedTimeframeAgreed && (
        <>
          <div className="advancePayment__banner">
            {paymentDue}
            <p className="advancePayment__amount">{`£${opportunityData.advancePayment}`}</p>
          </div>
          <PaymentSummary
            {...opportunityData}
            paymentPromptText="Please complete your Holding Deposit Payment below"
          />
          {!isAdvancePaymentPaid && (
            <>
              <Timer
                offerExpiresAt={offerExpiresAt}
                timerCompleted={timerCompleted}
              />
              <AsperatoIFrame
                src={url}
                iframeLoadingMessage={iframeMessage}
                appHistory={history}
                sucessPath={PATHS.ADVANCE_PAYMENT_SUCCESS}
              />
            </>
          )}
        </>
      )}
      {isAdvancePaymentPaid && (
        <Button
          title={nextButton}
          onClick={() => history.push(PATHS.ADVANCE_PAYMENT_SUCCESS)}
          disabled={!extendedTimeframeAgreed}
          testId="advance-next-button"
        />
      )}
    </>
  );
};

AdvancePayment.propTypes = {
  history: PropTypes.instanceOf(Object).isRequired,
};

export default AdvancePayment;
